/**
 * Coordinate model for the bingo board.
 *
 * Ported from the design handoff (`mockup/README.md`, "Coordinate model"). The maths here
 * must match that document exactly — if it drifts, exported cells stop lining up with what
 * the editor showed, which is the one bug in this project that is invisible until after the
 * user has done all their work.
 *
 * Two pixel spaces exist and must never be confused:
 *
 *   - **stage px** — the on-screen picture well. Depends on viewport and breakpoint.
 *   - **image px** — the picture's own native resolution. Independent of the screen.
 *
 * They are distinguished by a `space` discriminant carried at runtime, so mixing them is
 * both a compile error and visible in a debugger (ADR-0001, refined by ADR-0011).
 *
 * Canonical editor state is the **image-space** rect (ADR-0011): the board belongs to the
 * picture, not to the screen. Stage rects are derived for rendering and gestures only.
 */

/** Divisions per side. Fixed at 9 in the product; parameterised here (ADR-0006). */
export const GRID_N = 9;

/** Below this, a cell is too small to point at on screen. Sets the minimum board size. */
export const MIN_CELL_PX = 10;

/** Default board size on load, as a fraction of the fitted picture's shorter side. */
export const DEFAULT_FILL = 0.84;

export interface Size {
  readonly w: number;
  readonly h: number;
}

interface RectFields {
  /** Left edge. */
  readonly x: number;
  /** Top edge. */
  readonly y: number;
  /** Side length — the board is always square. */
  readonly size: number;
}

/** A board rect measured in on-screen stage pixels. */
export interface StageRect extends RectFields {
  readonly space: 'stage';
}

/** A board rect measured in the picture's native pixels. */
export interface ImageRect extends RectFields {
  readonly space: 'image';
}

export const stageRect = (x: number, y: number, size: number): StageRect => ({
  space: 'stage',
  x,
  y,
  size,
});

export const imageRect = (x: number, y: number, size: number): ImageRect => ({
  space: 'image',
  x,
  y,
  size,
});

/**
 * How the picture sits inside the stage, fitted *contain*-style.
 *
 * `s` converts image px to stage px; `fitX`/`fitY` are the letterbox offsets.
 */
export interface Fit {
  /** Scale factor: stage px per image px. */
  readonly s: number;
  /** Displayed picture width, in stage px. */
  readonly w: number;
  /** Displayed picture height, in stage px. */
  readonly h: number;
  /** Left offset of the displayed picture within the stage, in stage px. */
  readonly fitX: number;
  /** Top offset of the displayed picture within the stage, in stage px. */
  readonly fitY: number;
}

const assertPositive = (size: Size, label: string): void => {
  if (!(size.w > 0) || !(size.h > 0) || !Number.isFinite(size.w) || !Number.isFinite(size.h)) {
    throw new RangeError(
      `${label} must have finite positive dimensions, got ${size.w}x${size.h}`,
    );
  }
};

/**
 * Contain-fit the picture inside the stage.
 *
 *     s = min(SW / naturalW, SH / naturalH)
 *     w = naturalW * s;  h = naturalH * s
 *     fitX = (SW - w) / 2;  fitY = (SH - h) / 2
 */
export function containFit(stage: Size, natural: Size): Fit {
  assertPositive(stage, 'stage');
  assertPositive(natural, 'natural');

  const s = Math.min(stage.w / natural.w, stage.h / natural.h);
  const w = natural.w * s;
  const h = natural.h * s;

  return { s, w, h, fitX: (stage.w - w) / 2, fitY: (stage.h - h) / 2 };
}

/**
 * Stage px -> image px.
 *
 *     gx = (x - fitX) / s
 */
export function toImageRect(rect: StageRect, fit: Fit): ImageRect {
  return imageRect((rect.x - fit.fitX) / fit.s, (rect.y - fit.fitY) / fit.s, rect.size / fit.s);
}

/** Image px -> stage px. Exact inverse of {@link toImageRect}. */
export function toStageRect(rect: ImageRect, fit: Fit): StageRect {
  return stageRect(rect.x * fit.s + fit.fitX, rect.y * fit.s + fit.fitY, rect.size * fit.s);
}

/**
 * Movement and size limits for a given stage, in stage px.
 *
 * The handoff quotes fixed pairs for its two reference stages — size 90–640 and edge margin
 * 40 at mobile (402x436), size 120–900 and edge margin 60 at desktop (880x616). The stage is
 * measured rather than hard-coded here (ADR-0004), so these are derived from the stage
 * instead, with rules chosen to land on the quoted pairs. `edge` reproduces both exactly;
 * `minSize`/`maxSize` land within ~6% of them, which is immaterial for limits whose only job
 * is to stop the board becoming absurd. See geometry.test.ts, which pins the deviation.
 */
export interface Clamps {
  /** Smallest permitted board side, in stage px. */
  readonly minSize: number;
  /** Largest permitted board side, in stage px. */
  readonly maxSize: number;
  /** How much of the board must remain within the stage, in stage px. */
  readonly edge: number;
}

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value));

export function clampsFor(stage: Size, n: number = GRID_N): Clamps {
  assertPositive(stage, 'stage');
  const minDim = Math.min(stage.w, stage.h);

  return {
    minSize: Math.max(n * MIN_CELL_PX, Math.round(minDim * 0.2)),
    maxSize: Math.round(minDim * 1.5),
    edge: clamp(Math.round(minDim * 0.1), 40, 60),
  };
}

/**
 * Constrain a board rect to its stage.
 *
 * Size is clamped first, then position — the position limits depend on the size. The board
 * may hang off an edge but never leave the stage entirely: at least `edge` px stay visible
 * on every side.
 */
export function clampStageRect(rect: StageRect, stage: Size, n: number = GRID_N): StageRect {
  const { minSize, maxSize, edge } = clampsFor(stage, n);
  const size = clamp(rect.size, minSize, maxSize);

  return stageRect(
    clamp(rect.x, -size + edge, stage.w - edge),
    clamp(rect.y, -size + edge, stage.h - edge),
    size,
  );
}

/** Centre a board of the given size within the stage. */
export function centredStageRect(size: number, stage: Size): StageRect {
  return stageRect((stage.w - size) / 2, (stage.h - size) / 2, size);
}

/**
 * The board as it should appear on load: 84% of the fitted picture's shorter side, centred
 * in the stage, then clamped.
 */
export function defaultStageRect(stage: Size, fit: Fit, n: number = GRID_N): StageRect {
  const size = Math.round(Math.min(fit.w, fit.h) * DEFAULT_FILL);
  return clampStageRect(centredStageRect(size, stage), stage, n);
}

/**
 * The default board expressed directly in image pixels, with no stage involved.
 *
 * The stage cancels out. Writing `size_stage = 0.84 * s * min(natW, natH)` and dividing by
 * `s` gives `size_image = 0.84 * min(natW, natH)`; substituting `fitX = (SW - natW*s) / 2`
 * into `(SW - size_stage) / 2` and converting likewise gives `x_image = (natW - size) / 2`.
 * So "84% of the fitted picture, centred in the stage" is exactly "84% of the picture,
 * centred on the picture" — which is available before the stage has been measured.
 *
 * Unclamped: pass through {@link clampStageRect} once a stage is known. That only bites for
 * extreme panoramas, where the fitted picture is shorter than the minimum board size.
 */
export function defaultImageRect(natural: Size, fill: number = DEFAULT_FILL): ImageRect {
  assertPositive(natural, 'natural');
  const size = Math.round(Math.min(natural.w, natural.h) * fill);
  return imageRect((natural.w - size) / 2, (natural.h - size) / 2, size);
}

/** Side length of one cell, in whatever space the rect is measured in. */
export const cellSize = (rect: StageRect | ImageRect, n: number = GRID_N): number =>
  rect.size / n;

/**
 * Ruler label for a column: 0 -> "A". Generated rather than hard-coded so the module holds
 * no literal 9 (ADR-0006). Single-letter labels cap at 26 columns, which no reachable path
 * exceeds.
 */
export function columnLabel(index: number): string {
  return String.fromCharCode(65 + index);
}

/** Ruler label for a row: 0 -> "1". */
export function rowLabel(index: number): string {
  return String(index + 1);
}
